import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Globe,
  FileText,
  Users,
  Mail,
  Link2,
  Briefcase,
  Save,
  Loader2,
  Settings,
} from "lucide-react";
import {
  businesses as businessesApi,
  sites,
  type Business,
  type BusinessStatus,
  type Site,
} from "../lib/api";
import { usePolling } from "../hooks/use-polling";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-500/10 text-green-400",
  archived: "bg-gray-500/10 text-gray-400",
  planning: "bg-amber-500/10 text-amber-400",
};

const SITE_STATUS_COLORS: Record<string, string> = {
  active: "bg-green-500/10 text-green-400",
  inactive: "bg-gray-500/10 text-gray-400",
  pending: "bg-amber-500/10 text-amber-400",
  building: "bg-blue-500/10 text-blue-400",
  error: "bg-red-500/10 text-red-400",
};

type TabKey = "overview" | "websites" | "content" | "affiliates" | "audience" | "email";

const TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: "overview", label: "Overview", icon: Briefcase },
  { key: "websites", label: "Websites", icon: Globe },
  { key: "content", label: "Content", icon: FileText },
  { key: "affiliates", label: "Affiliates", icon: Link2 },
  { key: "audience", label: "Audience", icon: Users },
  { key: "email", label: "Email", icon: Mail },
];

const BRAND_VOICE_OPTIONS = [
  "professional",
  "casual",
  "technical",
  "friendly",
  "authoritative",
  "conversational",
];

export default function BusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<Business | null>(null);
  const [bizSites, setBizSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("overview");

  // Edit form state
  const [form, setForm] = useState({
    name: "",
    description: "",
    niche: "",
    targetAudience: "",
    brandVoice: "",
    status: "active" as BusinessStatus,
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      const [bRes, sRes] = await Promise.all([businessesApi.get(id), sites.list()]);
      const biz = bRes.data;
      setBusiness(biz);
      setBizSites(sRes.data.filter((s) => s.clusterId === id));
      setForm({
        name: biz.name ?? "",
        description: biz.description ?? "",
        niche: biz.niche ?? "",
        targetAudience: biz.targetAudience ?? "",
        brandVoice: biz.brandVoice ?? "",
        status: biz.status ?? "active",
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load business");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  usePolling(loadData, 15_000);

  async function handleSave() {
    if (!id) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      const res = await businessesApi.update(id, {
        name: form.name,
        ...(form.description ? { description: form.description } : {}),
        ...(form.niche ? { niche: form.niche } : {}),
        ...(form.targetAudience ? { targetAudience: form.targetAudience } : {}),
        ...(form.brandVoice ? { brandVoice: form.brandVoice } : {}),
        ...(form.status ? { status: form.status } : {}),
      });
      setBusiness(res.data);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch {
      setError("Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="max-w-5xl mx-auto py-12 text-center">
        <p className="text-sm text-gray-500">{error || "Business not found"}</p>
        <button
          onClick={() => navigate("/businesses")}
          className="mt-4 text-sm text-brand-400 hover:text-brand-300"
        >
          Back to Businesses
        </button>
      </div>
    );
  }

  const totalPages = bizSites.reduce((sum, s) => sum + (s.stats?.pageCount ?? 0), 0);
  const pagesByStatus = bizSites.reduce<Record<string, number>>((acc, s) => {
    const status = s.status || "unknown";
    acc[status] = (acc[status] ?? 0) + (s.stats?.pageCount ?? 0);
    return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Back + Header */}
      <div>
        <button
          onClick={() => navigate("/businesses")}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-300 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Businesses
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-600/20 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-brand-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-100">{business.name}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[business.status ?? "active"] ?? STATUS_COLORS["active"]}`}
                >
                  {business.status ?? "active"}
                </span>
                {business.niche && (
                  <span className="text-xs text-gray-500">{business.niche}</span>
                )}
                <span className="text-xs text-gray-600">
                  {bizSites.length} site{bizSites.length !== 1 ? "s" : ""} · {totalPages.toLocaleString()} pages
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-800 overflow-x-auto">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap ${
              activeTab === key ? "text-brand-400" : "text-gray-500 hover:text-gray-300"
            }`}
          >
            {label}
            {activeTab === key && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-500 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg text-sm border bg-red-500/10 border-red-500/20 text-red-400">
          {error}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-sm font-semibold text-gray-100 mb-4">Business Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Niche</label>
                <input
                  value={form.niche}
                  onChange={(e) => setForm((f) => ({ ...f, niche: e.target.value }))}
                  placeholder="e.g. home-fitness"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Status</label>
                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, status: e.target.value as BusinessStatus }))
                  }
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="active">Active</option>
                  <option value="planning">Planning</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  Brand Voice
                </label>
                <select
                  value={form.brandVoice}
                  onChange={(e) => setForm((f) => ({ ...f, brandVoice: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">Select...</option>
                  {BRAND_VOICE_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-full">
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  placeholder="What does this business do?"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                />
              </div>
              <div className="col-span-full">
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  Target Audience
                </label>
                <textarea
                  value={form.targetAudience}
                  onChange={(e) => setForm((f) => ({ ...f, targetAudience: e.target.value }))}
                  rows={2}
                  placeholder="Who is this business for?"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-5">
              <button
                onClick={() => void handleSave()}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? "Saving..." : "Save Changes"}
              </button>
              {saveSuccess && (
                <span className="text-xs text-green-400">Saved successfully</span>
              )}
            </div>
          </div>

          {/* Domain summary */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-sm font-semibold text-gray-100 mb-3">Domains</h2>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[10px] px-1.5 py-0.5 bg-brand-500/10 text-brand-400 rounded-full font-medium uppercase tracking-wide">
                  Hub
                </span>
                <span className="text-gray-200">{business.hubDomain}</span>
              </div>
              {business.spokeDomains.length > 0 && (
                <div className="space-y-1.5 mt-2">
                  {business.spokeDomains.map((d) => (
                    <div key={d} className="flex items-center gap-2 text-sm">
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-700/50 text-gray-400 rounded-full font-medium uppercase tracking-wide">
                        Spoke
                      </span>
                      <span className="text-gray-300">{d}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "websites" && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          {bizSites.length === 0 ? (
            <div className="p-12 text-center">
              <Globe className="w-10 h-10 text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No websites in this business yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Domain</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Pages</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 hidden sm:table-cell">
                    Role
                  </th>
                </tr>
              </thead>
              <tbody>
                {bizSites.map((site, i) => {
                  const role =
                    business.hubDomain === site.domain
                      ? "hub"
                      : business.spokeDomains.includes(site.domain)
                        ? "spoke"
                        : null;
                  return (
                    <tr
                      key={site._id}
                      onClick={() => navigate(`/websites/${site._id}`)}
                      className={`hover:bg-gray-800/40 transition-colors cursor-pointer ${
                        i < bizSites.length - 1 ? "border-b border-gray-800/50" : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Globe className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                          <span className="text-gray-200 font-medium">{site.domain}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${SITE_STATUS_COLORS[site.status] ?? ""}`}
                        >
                          {site.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-400">
                        {site.stats?.pageCount?.toLocaleString() ?? 0}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        {role === "hub" && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-brand-500/10 text-brand-400 rounded-full font-medium uppercase tracking-wide">
                            Hub
                          </span>
                        )}
                        {role === "spoke" && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-700/50 text-gray-400 rounded-full font-medium uppercase tracking-wide">
                            Spoke
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === "content" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-brand-600/20 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-brand-400" />
                </div>
                <span className="text-sm text-gray-400">Total Pages</span>
              </div>
              <p className="text-2xl font-bold text-gray-100">{totalPages.toLocaleString()}</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Globe className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-sm text-gray-400">Active Sites</span>
              </div>
              <p className="text-2xl font-bold text-gray-100">
                {bizSites.filter((s) => s.status === "active").length}
              </p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Settings className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-sm text-gray-400">Building</span>
              </div>
              <p className="text-2xl font-bold text-gray-100">
                {bizSites.filter((s) => s.status === "building").length}
              </p>
            </div>
          </div>

          {Object.keys(pagesByStatus).length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-gray-100 mb-3">Pages by Site Status</h3>
              <div className="space-y-2">
                {Object.entries(pagesByStatus).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between text-sm">
                    <span className="text-gray-400 capitalize">{status}</span>
                    <span className="text-gray-200 font-medium">{count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-site breakdown */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-gray-100 mb-3">Content by Website</h3>
            {bizSites.length === 0 ? (
              <p className="text-sm text-gray-500">No websites in this business.</p>
            ) : (
              <div className="space-y-2">
                {bizSites
                  .sort((a, b) => (b.stats?.pageCount ?? 0) - (a.stats?.pageCount ?? 0))
                  .map((site) => (
                    <div key={site._id} className="flex items-center justify-between text-sm">
                      <Link
                        to={`/websites/${site._id}`}
                        className="text-gray-300 hover:text-brand-400 transition-colors"
                      >
                        {site.domain}
                      </Link>
                      <span className="text-gray-400">
                        {(site.stats?.pageCount ?? 0).toLocaleString()} pages
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "affiliates" && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-gray-100 mb-2">Shared Affiliate Config</h2>
          <p className="text-xs text-gray-500 mb-4">
            Shared affiliate configuration applied across all websites in this business. Per-site
            overrides can be set on each website.
          </p>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">
              {business.sharedAffiliateConfig
                ? JSON.stringify(business.sharedAffiliateConfig, null, 2)
                : "No shared config set."}
            </pre>
          </div>
        </div>
      )}

      {activeTab === "audience" && (
        <div className="bg-gray-900 border border-gray-800 border-dashed rounded-xl p-12 text-center">
          <Users className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-gray-200 mb-1">Audience</h3>
          <p className="text-sm text-gray-500">
            Audience segmentation and tracking coming soon.
          </p>
        </div>
      )}

      {activeTab === "email" && (
        <div className="bg-gray-900 border border-gray-800 border-dashed rounded-xl p-12 text-center">
          <Mail className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-gray-200 mb-1">Email</h3>
          <p className="text-sm text-gray-500">
            Email campaign management and warmup tracking coming soon.
          </p>
        </div>
      )}
    </div>
  );
}
